import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';

import { Flex, FlexColumn } from 'src/components/flex';
import type { ISnippet } from 'src/services/api';
import Switch from 'src/components/input/switch/Switch';
// import { ELeadStage } from 'src/services/api';
// import Select from 'src/components/input/select/Select';
import SnippetTemplateMessage from 'src/pages/landlord/snippets/components/form/snippet-template-message';
import { useCreateSnippet, useUpdateSnippet } from 'src/services/api';
import { useLoggedInUser } from 'src/services/api';
import { formatPlaybookMessageForBackend } from 'src/common/playbook-variables/playbook-variable-utils';

type Form = {
    name: string;
    contents: string;
    is_private: boolean;
    // TODO: uncomment for new_lead_stage
    // new_lead_stage?: string | null;
};

type InitialValues = {
    name?: string;
    contents?: string;
    is_private?: boolean;
};

interface Props {
    snippet?: ISnippet;
    initialValues?: InitialValues;
    onClose: () => void;
}

export default function SnippetForm({ snippet, initialValues, onClose }: Props) {
    const { mutateAsync: createSnippet } = useCreateSnippet();
    const { mutateAsync: updateSnippet } = useUpdateSnippet(snippet?.id);
    const { data: loggedInUser } = useLoggedInUser();
    const title = snippet ? 'Edit Snippet' : initialValues ? 'New Snippet from Template' : 'New Snippet';

    const truncatedSnippetContents = snippet && snippet?.contents.length > 2000 ? snippet.contents.slice(0, 2000) : snippet?.contents;
    const form = useForm<Form>({
        mode: 'onBlur',
        defaultValues: {
            name: snippet?.name || initialValues?.name || '',
            contents: truncatedSnippetContents || initialValues?.contents || '',
            is_private: snippet?.is_private ?? initialValues?.is_private ?? false,
        }
    });

    // TODO: uncomment for new_lead_stage
    // const [ isChangeLeadStage, setIsChangeLeadStage ] = useState(false);

    // const handleToggleChangeStage = () => {
    //     setIsChangeLeadStage(!isChangeLeadStage);
    //     form.setValue('new_lead_stage', null);
    // };

    const submit = async (values: Form) => {
        const formattedValues = {
            name: values.name,
            contents: formatPlaybookMessageForBackend(values.contents),
            is_private: values.is_private,
        };
        if (snippet) {
            await updateSnippet(formattedValues);
        } else {
            await createSnippet(formattedValues);
        }
        onClose();
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(submit)}>
                <DialogTitle>
                    <Flex justifyContent="space-between" alignItems="center" mb={2}>
                        {title}
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Flex>
                </DialogTitle>
                <DialogContent>
                    <FlexColumn gap={2} mt={1}>
                        <TextField
                            variant="outlined"
                            label="Name"
                            placeholder="Name"
                            fullWidth
                            {...form.register('name')}
                            inputProps={{
                                maxLength: 50,
                            }}
                        />

                        <Tooltip
                            title={snippet?.creator_id !== loggedInUser?.id ? 'Only the snippet creator can change this' : ''}
                        >
                            <span style={{ display: 'inline-block' }}>
                                <Switch
                                    label="Shared With Team"
                                    value={!form.watch('is_private')}
                                    onChange={(value) => form.setValue('is_private', !value)}
                                    disabled={snippet && snippet?.creator_id !== loggedInUser?.id}
                                />
                            </span>
                        </Tooltip>

                        <SnippetTemplateMessage value={form.watch('contents')} onChange={(newValue) => form.setValue('contents', newValue)} />

                        {/* TODO: uncomment for new_lead_stage */}
                        {/* <Switch
                            label="Change Stage"
                            value={isChangeLeadStage}
                            onChange={handleToggleChangeStage}
                        />
                        {isChangeLeadStage && (
                            <Select
                                label="Select a lead stage"
                                size="small"
                                options={stageOptions}
                                fullWidth
                                value={form.watch('new_lead_stage') ?? null}
                                onChange={(value) => form.setValue('new_lead_stage', value as string)}
                            />
                        )} */}
                    </FlexColumn>
                </DialogContent>
                <DialogActions>
                    <Flex p={2}>
                        {!snippet &&
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                loading={form.formState.isSubmitting}
                            >
                                Create Snippet
                            </LoadingButton>
                        }
                        {!!snippet &&
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                loading={form.formState.isSubmitting}
                            >
                                Update Snippet
                            </LoadingButton>
                        }
                    </Flex>
                </DialogActions>
            </form>
        </FormProvider>
    );
}